import { Card } from "@mui/material";
import BackImg from "@assets/images/stepsBack.jpg";
import NoHistory from "./NoData";
import { useState, useEffect, useRef } from "react";
import { fetchLifestyleWellnessData } from "api/weeklyTracker";

const LifestyleWellnessOverview = ({ metrics = {}, t }) => {
  // Extract current week and total weeks from metrics
  const currentWeekFromMetrics = metrics?.current_week || 1;
  const totalWeeksFromMetrics = metrics?.total_weeks || 1;

  // State for component
  const [selectedWeek, setSelectedWeek] = useState(currentWeekFromMetrics);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMetrics, setCurrentMetrics] = useState(metrics);

  const formattedKeys = {
    energy_level: currentMetrics?.energy_level,
    motivation_level: currentMetrics?.motivation_level,
    sleep_quality: currentMetrics?.sleep_quality,
    stress_level: currentMetrics?.stress_level,
  };

  const metricsArray = Object.entries(formattedKeys)?.map(([key, value]) => ({
    title: key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" "),
    value: value ? parseInt(value) : 0,
  }));

  const hasData =
    metricsArray.length > 0 &&
    metricsArray.some(
      (m) => m.value !== null && m.value !== undefined && m.value > 0
    );
  const maxValue = 10;

  // State for animated widths
  const [animatedWidths, setAnimatedWidths] = useState(
    metricsArray.map(() => 0)
  );

  // Ref for the component to observe
  const componentRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  // Fetch data for a specific week
  const fetchWeekData = async (week) => {
    // 1. Reset animation widths immediately when data fetch starts
    setAnimatedWidths(metricsArray.map(() => 0));

    if (week === currentWeekFromMetrics) {
      setCurrentMetrics(metrics);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchLifestyleWellnessData(week);
      setCurrentMetrics(response?.lifestyle_wellness);
    } catch (error) {
      console.error("Error fetching week data:", error);
      // Keep current data on error
    } finally {
      setIsLoading(false);
    }
  };

  // Handle week change
  const handleWeekChange = (event) => {
    const newWeek = parseInt(event.target.value);
    setSelectedWeek(newWeek);
    fetchWeekData(newWeek);
  };

  // Intersection Observer to detect when component comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          // Don't unobserve here, as we need it to remain 'true' if the user scrolls it back into view.
          // We can let the cleanup function handle the unobserve on unmount.
        } else {
          // Optional: Reset isInView if component leaves the viewport
          setIsInView(false);
          // Optional: Reset animation when component scrolls OUT of view
          setAnimatedWidths(metricsArray.map(() => 0));
        }
      },
      {
        threshold: 0.3,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
      }
    };
  }, [currentMetrics]); // Dependency added to re-observe if metrics change drastically

  // 🚀 COMBINED ANIMATION EFFECT - Triggers on isInView change OR metrics change
  useEffect(() => {
    // Only proceed if there is data AND the component is in view
    if (hasData && isInView) {
      // Set the initial state to 0 widths
      setAnimatedWidths(metricsArray.map(() => 0));

      // Wait a moment (150ms) before starting the transition
      const timer = setTimeout(() => {
        const newWidths = metricsArray.map((metric) =>
          Math.min((metric.value / maxValue) * 100, 100)
        );
        setAnimatedWidths(newWidths);
      }, 150);

      return () => clearTimeout(timer);
    }
    // If it scrolls out of view (isInView = false), it's already reset by the Observer.
  }, [currentMetrics, hasData, isInView]);

  // Initial setup: ensure state is synced with props
  useEffect(() => {
    if (metrics?.current_week) {
      setSelectedWeek(metrics.current_week);
      setCurrentMetrics(metrics);
    }
  }, [metrics]);

  const weekOptions = Array.from(
    { length: totalWeeksFromMetrics },
    (_, i) => i + 1
  );

  return (
    <div
      ref={componentRef}
      className="w-full p-6 border-none shadow-lg !rounded-3xl bg-cover"
      style={{ backgroundImage: `url(${BackImg})` }}
    >
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-2 mb-6">
        <h2 className="text-white text-xl font-semibold">
          {t("Lifestyle & Wellness Overview")}
        </h2>

        {/* Week Selection Dropdown */}
        {totalWeeksFromMetrics > 0 && (
          <div className="flex items-center gap-2">
            {isLoading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin hidden md:block"></div>
            )}
            <label
              htmlFor="week-select"
              className="text-white text-sm font-medium"
            >
              {t("Week")}:
            </label>
            <select
              id="week-select"
              value={selectedWeek}
              onChange={handleWeekChange}
              disabled={isLoading}
              className="bg-white rounded-lg px-3 py-1 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[80px]"
            >
              {weekOptions.map((week) => (
                <option key={week} value={week}>
                  {week} {week === currentWeekFromMetrics ? "(Current)" : ""}
                </option>
              ))}
            </select>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin block md:hidden"></div>
            )}
          </div>
        )}
      </div>

      <div className={`bg-white rounded-2xl p-6 min-h-[200px] relative`}>
        {!hasData ? (
          <NoHistory
            heading="No Weekly Check-In Data Available"
            subheading={`No lifestyle and wellness data available for week ${selectedWeek}. Complete your weekly check-in to see your overview here.`}
          />
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {t("Week")} {selectedWeek} {t("Overview")}
              </h3>
              {selectedWeek === currentWeekFromMetrics && (
                <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                  {t("Current Week")}
                </span>
              )}
            </div>

            <div className="space-y-3 py-2 border-l border-b border-b-[#C4C4C4]">
              {metricsArray?.map((metric, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-black">
                      {t(metric.title)}
                    </span>
                    <span className="text-xs text-gray-500 font-medium">
                      {metric.value}/10
                    </span>
                  </div>
                  <div className="relative h-8 overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-r-[10px] transition-all duration-1000 ease-out flex items-center justify-start pl-3"
                      style={{
                        width: `${animatedWidths[index]}%`,
                        transitionDelay: `${index * 100}ms`,
                      }}
                    >
                      <span className="text-white text-sm font-semibold">
                        {metric.value}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between mt-4 px-1">
              {Array.from({ length: 11 }, (_, i) => i).map((num) => (
                <span
                  key={num}
                  className="text-xs text-muted-foreground font-medium"
                >
                  {num}
                </span>
              ))}
            </div>

            {/* If you want a loading overlay for data fetching:
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-2xl z-10">
                <div className="w-8 h-8 border-[5px] border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            */}
          </>
        )}
      </div>
    </div>
  );
};

export default LifestyleWellnessOverview;
